import { marked } from 'marked';
import { Language } from './i18n';

// Type definitions for team members
export interface TeamMember {
  id: string;
  name: {
    [key in Language]: string;
  };
  role: {
    [key in Language]: string;
  };
  area: {
    [key in Language]: string;
  };
  message: {
    [key in Language]: string;
  };
  details: {
    [key in Language]: string;
  };
  image: string;
}

/**
 * Checks if an image exists at the given path
 * @param imagePath - URL or path to the image
 */
async function checkImageExists(imagePath: string): Promise<boolean> {
  if (imagePath.startsWith('http')) {
    return true;
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = imagePath;
  });
}

/**
 * Gets the appropriate image URL, falling back to default if necessary
 * @param localImage - Local image path or URL
 * @param defaultImage - Default image URL to use as fallback
 */
async function getImageUrl(localImage: string, defaultImage: string): Promise<string> {
  // If it's already an external URL, return as is
  if (localImage.startsWith('http')) {
    return localImage;
  }

  // Construct the full path for local image
  const fullPath = `/content/about_us/images/${localImage}`;
  
  try {
    const exists = await checkImageExists(fullPath);
    if (!exists) {
      console.warn(`Team member image not found: ${fullPath}, using default: ${defaultImage}`);
      return defaultImage;
    }
    return fullPath;
  } catch (error) {
    console.error('Error checking team member image:', error);
    return defaultImage;
  }
}

export async function getAboutContent(language: Language): Promise<string> {
  try {
    const response = await fetch(`/content/about_us/${language}.md`);
    if (!response.ok) {
      throw new Error('Failed to load about content');
    }
    const text = await response.text();
    return marked(text);
  } catch (error) {
    console.error('Error loading about content:', error);
    return '';
  }
}

export async function getAllTeamMembers(): Promise<TeamMember[]> {
  try {
    const response = await fetch('/content/about_us/members.json');
    if (!response.ok) {
      throw new Error('Failed to load team members');
    }
    const data = await response.json();
    
    // Process each member's image with fallback handling
    const processedMembers = await Promise.all(
      data.members.map(async (member: any) => ({
        ...member,
        image: await getImageUrl(member.image, data.defaultImage),
        // Parse markdown in details
        details: {
          de: marked(member.details.de.split('*').join('\n*')),
          ja: marked(member.details.ja.split('*').join('\n*'))
        }
      }))
    );

    return processedMembers;
  } catch (error) {
    console.error('Error loading team members:', error);
    return [];
  }
}

export async function getTeamMemberById(id: string): Promise<TeamMember | null> {
  try {
    const members = await getAllTeamMembers();
    return members.find(member => member.id === id) || null;
  } catch (error) {
    console.error('Error loading team member:', error);
    return null;
  }
}